import type {
  FoundationComponentEntity,
  FootingEntity,
  StemWallEntity,
  CurbEntity,
  PierEntity,
  StripFootingEntity,
  CircularIsolatedFootingEntity,
  RectangularIsolatedFootingEntity,
  TurndownFootingEntity,
  CircularCMUPierEntity,
  RectangularCMUPierEntity,
  CircularCIPPierEntity,
  RectangularCIPPierEntity,
  CustomPierEntity,
  CIPStemWallEntity,
  CMUStemWallEntity,
  FixedWidthCurbEntity,
  AutoWidthCurbEntity,
} from "building-types/foundations/foundationComponentEntityTypes";
import type {
  TurndownBrickLedgeType,
  CIPStemWallBrickLedgeType,
  CMUBrickLedgeType,
} from "building-types/foundations/foundationComponentEntityTypes";
import { FoundationComponentType } from "building-types/foundations/foundationComponentEntityTypes";
import {
  FootingType,
  BrickLedgeType,
  BrickLedgeBehavior,
  PierType,
  StemWallType,
  CurbType,
  FoundationSubSystemType,
} from "building-types/foundations/foundationEnums";
import type { Table } from "common/data/table";
import { assertUnreachable } from "common/utils/assertUnreachable";
import uid from "common/utils/uid";
import type { PolyLine2 } from "geom/polyLine2Types";

export function isFooting(component: FoundationComponentEntity): component is FootingEntity {
  return component && component.componentType === FoundationComponentType.FOOTING;
}

export function isStemWall(component: FoundationComponentEntity): component is StemWallEntity {
  return component && component.componentType === FoundationComponentType.STEMWALL;
}

export function isCurb(component: FoundationComponentEntity): component is CurbEntity {
  return component && component.componentType === FoundationComponentType.CURB;
}

export function isPier(component: FoundationComponentEntity): component is PierEntity {
  return component && component.componentType === FoundationComponentType.PIER;
}

export function foundationComponentCopy(
  component: FoundationComponentEntity,
  name?: string,
): FoundationComponentEntity {
  return {
    ...component,
    name: name ?? component.name,
  };
}

export function isStripFooting(footing: FootingEntity): footing is StripFootingEntity {
  return footing && footing.footingType === FootingType.STRIP;
}

export function isCircularIsolatedFooting(footing: FootingEntity): footing is CircularIsolatedFootingEntity {
  return footing && footing.footingType === FootingType.CIRCULAR_ISOLATED;
}

export function isRectangularIsolatedFooting(footing: FootingEntity): footing is RectangularIsolatedFootingEntity {
  return footing && footing.footingType === FootingType.RECTANGULAR_ISOLATED;
}

export function stripFootingNew(
  name: string,
  footingWidth: number,
  footingDepth: number,
  footingPadding: number = 0,
  lateralOffset?: number,
): StripFootingEntity {
  return {
    entityType: "FOUNDATION_COMPONENT",
    id: uid() as any,
    sortIndex: 0,
    componentType: FoundationComponentType.FOOTING,
    footingType: FootingType.STRIP,
    name,
    footingWidth,
    footingDepth,
    footingPadding,
    lateralOffset,
  };
}

export function circularIsolatedFootingNew(
  name: string,
  footingRadius: number,
  footingDepth: number,
): CircularIsolatedFootingEntity {
  return {
    entityType: "FOUNDATION_COMPONENT",
    id: uid() as any,
    sortIndex: 0,
    componentType: FoundationComponentType.FOOTING,
    footingType: FootingType.CIRCULAR_ISOLATED,
    name,
    footingRadius,
    footingDepth,
  };
}

export function rectangularIsolatedFootingNew(
  name: string,
  footingLength: number,
  footingWidth: number,
  footingDepth: number,
): RectangularIsolatedFootingEntity {
  return {
    entityType: "FOUNDATION_COMPONENT",
    id: uid() as any,
    sortIndex: 0,
    componentType: FoundationComponentType.FOOTING,
    footingType: FootingType.RECTANGULAR_ISOLATED,
    name,
    footingLength,
    footingWidth,
    footingDepth,
  };
}

export function isTurndownFooting(footing: FootingEntity): footing is TurndownFootingEntity {
  return footing.footingType === FootingType.TURNDOWN;
}

export function turndownFootingNew(
  name: string,
  footingWidth: number,
  brickLedgeType: TurndownBrickLedgeType = BrickLedgeType.NONE,
  brickLedgeWidth: number = 0,
): TurndownFootingEntity {
  return {
    entityType: "FOUNDATION_COMPONENT",
    id: uid() as any,
    sortIndex: 0,
    componentType: FoundationComponentType.FOOTING,
    footingType: FootingType.TURNDOWN,
    name,
    footingWidth,
    brickLedgeBehavior: BrickLedgeBehavior.AUTO,
    brickLedgeType,
    brickLedgeWidth,
  };
}

export function circularCMUPierNew(name: string, pierRadius: number, pierDepth?: number): CircularCMUPierEntity {
  return {
    entityType: "FOUNDATION_COMPONENT",
    id: uid(),
    sortIndex: 0,
    componentType: FoundationComponentType.PIER,
    pierType: PierType.CIRCULAR_CMU,
    name,
    pierRadius,
    pierDepth,
  };
}

export function isCircularCMUPier(pier: PierEntity): pier is CircularCMUPierEntity {
  return pier.pierType === PierType.CIRCULAR_CMU;
}

export function rectangularCMUPierNew(
  name: string,
  pierLength: number,
  pierWidth: number,
  pierDepth?: number,
): RectangularCMUPierEntity {
  return {
    entityType: "FOUNDATION_COMPONENT",
    id: uid() as any,
    sortIndex: 0,
    componentType: FoundationComponentType.PIER,
    pierType: PierType.RECTANGULAR_CMU,
    name,
    pierLength,
    pierWidth,
    pierDepth,
  };
}

export function isRectangularCMUPier(pier: PierEntity): pier is RectangularCMUPierEntity {
  return pier.pierType === PierType.RECTANGULAR_CMU;
}

export function circularCIPPierNew(name: string, pierRadius: number, pierDepth?: number): CircularCIPPierEntity {
  return {
    entityType: "FOUNDATION_COMPONENT",
    id: uid() as any,
    sortIndex: 0,
    componentType: FoundationComponentType.PIER,
    pierType: PierType.CIRCULAR_CIP,
    name,
    pierRadius,
    pierDepth,
  };
}

export function isCircularCIPPier(pier: PierEntity): pier is CircularCIPPierEntity {
  return pier.pierType === PierType.CIRCULAR_CIP;
}

export function rectangularCIPPierNew(
  name: string,
  pierLength: number,
  pierWidth: number,
  pierDepth?: number,
): RectangularCIPPierEntity {
  return {
    entityType: "FOUNDATION_COMPONENT",
    id: uid() as any,
    sortIndex: 0,
    componentType: FoundationComponentType.PIER,
    pierType: PierType.RECTANGULAR_CIP,
    name,
    pierLength,
    pierWidth,
    pierDepth,
  };
}

export function isRectangularCIPPier(pier: PierEntity): pier is RectangularCIPPierEntity {
  return pier.pierType === PierType.RECTANGULAR_CIP;
}

export function customPierNew(name: string, pierPoints: PolyLine2): CustomPierEntity {
  return {
    entityType: "FOUNDATION_COMPONENT",
    id: uid() as any,
    sortIndex: 0,
    componentType: FoundationComponentType.PIER,
    pierType: PierType.CUSTOM,
    name,
    pierPoints: pierPoints,
  };
}

export function isCustomPier(pier: PierEntity): pier is CustomPierEntity {
  return pier.pierType === PierType.CUSTOM;
}

export function CIPStemWallNew(
  name: string,
  stemWallWidth: number,
  brickLedgeType: CIPStemWallBrickLedgeType = BrickLedgeType.NONE,
  brickLedgeWidth: number = 0,
): CIPStemWallEntity {
  return {
    entityType: "FOUNDATION_COMPONENT",
    id: uid() as any,
    sortIndex: 0,
    componentType: FoundationComponentType.STEMWALL,
    stemWallType: StemWallType.CIP,
    name,
    stemWallWidth,
    brickLedgeBehavior: BrickLedgeBehavior.AUTO,
    brickLedgeType,
    brickLedgeWidth,
  };
}

export function CMUStemWallNew(
  name: string,
  stemWallBlockWidth: number,
  brickLedgeType: CMUBrickLedgeType = BrickLedgeType.NONE,
  stemWallBrickLedgeBlockWidth: number = stemWallBlockWidth,
): CMUStemWallEntity {
  return {
    entityType: "FOUNDATION_COMPONENT",
    id: uid() as any,
    sortIndex: 0,
    componentType: FoundationComponentType.STEMWALL,
    stemWallType: StemWallType.CMU,
    name,
    stemWallBlockWidth,
    brickLedgeBehavior: BrickLedgeBehavior.AUTO,
    brickLedgeType,
    stemWallBrickLedgeBlockWidth,
  };
}

export function fixedWidthCurbNew(name: string, curbWidth: number): FixedWidthCurbEntity {
  return {
    entityType: "FOUNDATION_COMPONENT",
    id: uid() as any,
    sortIndex: 0,
    componentType: FoundationComponentType.CURB,
    curbType: CurbType.FIXED_WIDTH,
    name,
    curbWidth,
  };
}

export function autoWidthCurbNew(name: string, id?: string): AutoWidthCurbEntity {
  return {
    entityType: "FOUNDATION_COMPONENT",
    id: id ?? (uid() as any),
    sortIndex: 0,
    componentType: FoundationComponentType.CURB,
    curbType: CurbType.AUTO_WIDTH,
    name,
  };
}

export function isValidFoundationAssemblyComponent(
  foundationComponents: Table<FoundationComponentEntity>,
  foundationSubSystem: FoundationSubSystemType,
  componentId: string,
): boolean {
  const component = foundationComponents[componentId];
  if (!component) {
    return false;
  }

  switch (component.componentType) {
    case FoundationComponentType.FOOTING: {
      const footing = component as FootingEntity;
      switch (footing.footingType) {
        case FootingType.STRIP: {
          return (
            foundationSubSystem !== FoundationSubSystemType.MONOLITHIC &&
            foundationSubSystem !== FoundationSubSystemType.ISOLATED
          );
        }
        case FootingType.TURNDOWN: {
          return foundationSubSystem === FoundationSubSystemType.MONOLITHIC;
        }
        case FootingType.CIRCULAR_ISOLATED:
        case FootingType.RECTANGULAR_ISOLATED: {
          return foundationSubSystem === FoundationSubSystemType.ISOLATED;
        }
        default: {
          assertUnreachable(footing);
        }
      }
    }
    case FoundationComponentType.STEMWALL: {
      const stemWall = component as StemWallEntity;
      switch (stemWall.stemWallType) {
        case StemWallType.CIP: {
          return foundationSubSystem === FoundationSubSystemType.STEM_WALL_CIP;
        }
        case StemWallType.CMU: {
          return foundationSubSystem === FoundationSubSystemType.STEM_WALL_CMU;
        }
        default: {
          assertUnreachable(stemWall);
        }
      }
    }
    case FoundationComponentType.CURB: {
      return true; // curbs are autogenerated
    }
    case FoundationComponentType.PIER: {
      const pier = component as PierEntity;
      switch (pier.pierType) {
        case PierType.CIRCULAR_CMU:
        case PierType.RECTANGULAR_CMU:
        case PierType.CIRCULAR_CIP:
        case PierType.RECTANGULAR_CIP:
        case PierType.CUSTOM: {
          return foundationSubSystem === FoundationSubSystemType.ISOLATED;
        }
        default: {
          assertUnreachable(pier);
        }
      }
    }
    default: {
      assertUnreachable(component);
    }
  }
}
